import { Component, EventEmitter, HostListener, OnInit, Output } from '@angular/core';
import { Router } from '@angular/router';
import { of } from 'rxjs';
import { finalize, map, switchMap } from 'rxjs/operators';
import { marked } from 'marked';

import { AuthService } from '../../../login/auth.service';
import { TokenStorageService } from '../../../login/token-storage.service';
import {
  AssistantMessage,
  AssistantWhatsappLink,
  AssistantService,
  AssistantThreadSummary,
} from '../../../services/assistant.service';

/** Backend cuts the auto title at 60 (assistant_v1.js MAX_TITLE_LENGTH). */
const TITLE_LENGTH = 60;

/**
 * P.A. overlay under the dashboard header (T199, I.pa fe line, C22).
 * Created on open, destroyed on close — every open loads the thread list from
 * the server and resumes the newest thread, so nothing carries over to the
 * next tenant after a re-login. A new chat is created lazily on its first
 * question. A 401 {code:"session_expired"} swaps the input for a re-login
 * prompt and sends nothing else (V179).
 */
@Component({
    selector: 'app-assistant-overlay',
    templateUrl: './assistant-overlay.component.html',
    styleUrls: ['./assistant-overlay.component.scss'],
    standalone: false
})
export class AssistantOverlayComponent implements OnInit {
  @Output() closed = new EventEmitter<void>();

  threads: AssistantThreadSummary[] = [];
  threadId: number | null = null;
  title: string | null = null;
  messages: AssistantMessage[] = [];
  draft = '';
  sending = false;
  expired = false;
  error: string | null = null;
  errorParams: Record<string, string> = {};
  historyOpen = false;
  editingId: number | null = null;
  editTitle = '';
  private readonly answerCache = new Map<string, string>();
  whatsappOpen = false;
  whatsappAllowed = false;
  whatsappLinks: AssistantWhatsappLink[] = [];
  whatsappPendingId: string | null = null;
  whatsappBusy = false;
  whatsappNumber: string | null = null;
  whatsappCode: string | null = null;
  whatsappError = false;

  constructor(
    private assistant: AssistantService,
    private auth: AuthService,
    private router: Router,
    private tokenStorage: TokenStorageService
  ) {
    this.whatsappAllowed = tokenStorage.hasPermission('profile.manage');
  }

  ngOnInit(): void {
    this.loadThreads(true);
  }

  toggleWhatsapp(): void {
    this.whatsappOpen = !this.whatsappOpen;
    if (this.whatsappOpen) this.refreshWhatsapp();
  }

  refreshWhatsapp(): void {
    this.assistant.whatsappLink().subscribe({
      next: (link) => {
        this.whatsappLinks = link.links;
        if (link.links.some((item) => item.id === this.whatsappPendingId)) {
          this.whatsappCode = null;
          this.whatsappPendingId = null;
        }
        this.whatsappError = false;
      },
      error: () => this.whatsappError = true,
    });
  }

  createWhatsappLink(): void {
    if (this.whatsappBusy) return;
    this.whatsappBusy = true;
    this.assistant.createWhatsappLink().pipe(finalize(() => this.whatsappBusy = false)).subscribe({
      next: (link) => {
        this.whatsappPendingId = link.id;
        this.whatsappCode = link.code;
        this.whatsappNumber = link.whatsappNumber;
        this.whatsappError = false;
      },
      error: () => this.whatsappError = true,
    });
  }

  removeWhatsappLink(id: string): void {
    if (this.whatsappBusy) return;
    this.whatsappBusy = true;
    this.assistant.removeWhatsappLink(id).pipe(finalize(() => this.whatsappBusy = false)).subscribe({
      next: () => {
        this.whatsappLinks = this.whatsappLinks.filter((link) => link.id !== id);
        this.whatsappError = false;
      },
      error: () => this.whatsappError = true,
    });
  }

  @HostListener('document:keydown.escape')
  close(): void {
    this.closed.emit();
  }

  toggleHistory(): void {
    this.historyOpen = !this.historyOpen;
    if (this.historyOpen) this.loadThreads(false);
  }

  openThread(id: number): void {
    this.assistant.getThread(id).subscribe({
      next: (thread) => {
        this.threadId = thread.id;
        this.title = thread.title;
        this.messages = thread.messages;
        this.error = null;
        this.historyOpen = false;
      },
      error: (e) => this.fail(e, 'ASSISTANT.ERROR_LOAD'),
    });
  }

  newChat(): void {
    this.threadId = null;
    this.title = null;
    this.messages = [];
    this.error = null;
    this.historyOpen = false;
  }

  onEnter(event: Event): void {
    if ((event as KeyboardEvent).shiftKey) return;
    event.preventDefault();
    this.send();
  }

  send(): void {
    const content = this.draft.trim();
    if (!content || this.sending || this.expired) return;

    const pending: AssistantMessage = { id: 0, role: 'user', content, created_at: '' };
    this.messages = [...this.messages, pending];
    this.draft = '';
    this.error = null;
    this.sending = true;

    const threadId$ =
      this.threadId !== null
        ? of(this.threadId)
        : this.assistant.createThread().pipe(map(({ id }) => (this.threadId = id)));

    threadId$
      .pipe(
        switchMap((id) => this.assistant.sendMessage(id, content)),
        finalize(() => (this.sending = false))
      )
      .subscribe({
        next: ({ userMessage, assistantMessage }) => {
          this.messages = [...this.messages.filter((m) => m !== pending), userMessage, assistantMessage];
          this.title = this.title ?? content.slice(0, TITLE_LENGTH);
        },
        error: (e) => {
          this.messages = this.messages.filter((m) => m !== pending);
          this.draft = content;
          this.fail(e, 'ASSISTANT.ERROR_UNAVAILABLE');
        },
      });
  }

  startRename(thread: AssistantThreadSummary): void {
    this.editingId = thread.id;
    this.editTitle = thread.title || '';
  }

  saveRename(thread: AssistantThreadSummary): void {
    const title = this.editTitle.trim();
    if (!title) {
      this.editingId = null;
      return;
    }
    this.assistant.renameThread(thread.id, title).subscribe({
      next: (renamed) => {
        thread.title = renamed.title;
        if (this.threadId === thread.id) this.title = renamed.title;
        this.editingId = null;
      },
      error: (e) => this.fail(e, 'ASSISTANT.ERROR_ACTION'),
    });
  }

  deleteThread(thread: AssistantThreadSummary): void {
    this.assistant.deleteThread(thread.id).subscribe({
      next: () => {
        this.threads = this.threads.filter((t) => t.id !== thread.id);
        if (this.threadId === thread.id) this.newChat();
      },
      error: (e) => this.fail(e, 'ASSISTANT.ERROR_ACTION'),
    });
  }

  /**
   * Reply markdown → HTML string. Rendered through Angular's [innerHTML]
   * sanitizer, which is the only guard against markup in tool data (V181).
   * Cached per content so change detection does not re-parse.
   */
  answerHtml(content: string): string {
    let html = this.answerCache.get(content);
    if (html === undefined) {
      html = marked.parse(content, { async: false, gfm: true, breaks: true }) as string;
      this.answerCache.set(content, html);
    }
    return html;
  }

  relogin(): void {
    this.auth.logout();
    this.router.navigate(['/login']);
  }

  private loadThreads(resume: boolean): void {
    this.assistant.listThreads().subscribe({
      next: (threads) => {
        this.threads = threads;
        if (resume && threads.length) this.openThread(threads[0].id);
      },
      error: (e) => this.fail(e, 'ASSISTANT.ERROR_LOAD'),
    });
  }

  private fail(e: any, fallbackKey: string): void {
    if (e?.status === 401 && e.error?.code === 'session_expired') {
      this.expired = true;
      return;
    }
    this.errorParams = {};
    if (e?.status === 429 && e.error?.code === 'usage_limit') {
      // V182: retryAt as the viewer's local clock; the draft stays, nothing re-sends.
      const retry = new Date(e.error.retryAt);
      this.errorParams = { time: isNaN(retry.getTime()) ? '' : retry.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) };
      this.error = 'ASSISTANT.ERROR_USAGE_LIMIT';
      return;
    }
    this.error = e?.error?.code === 'PA_TIMEOUT' ? 'ASSISTANT.ERROR_TIMEOUT' : fallbackKey;
  }
}
